import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarClock, CheckCircle2, AlertTriangle, Link2, Loader2, RefreshCw, Unlink, ExternalLink, FilePlus2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/fiscal-books`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtDate = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });

function BookCard({ year, kind, book, canEdit, onChanged }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState('');
  const link = async () => {
    if (!url.trim()) return;
    setBusy('link');
    try {
      const r = await axios.put(`${API}/${year.year}/${kind.key}`, { url: url.trim() }, auth());
      toast.success(`${year.long} ${kind.label} linked: ${r.data.book?.title || 'ok'}`);
      setUrl(''); onChanged(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not link that sheet'); } finally { setBusy(''); }
  };
  const verify = async () => {
    setBusy('verify');
    try { const r = await axios.post(`${API}/${year.year}/${kind.key}/verify`, {}, auth()); toast[r.data.ok ? 'success' : 'error'](r.data.ok ? `${kind.label} ${year.long} looks good` : (r.data.book?.error || 'Problem with this book')); onChanged(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Verify failed'); } finally { setBusy(''); }
  };
  const unlink = async () => {
    if (!window.confirm(`Unlink the ${year.long} ${kind.label} book from Ops?`)) return;
    setBusy('unlink');
    try { const r = await axios.delete(`${API}/${year.year}/${kind.key}`, auth()); toast.success('Unlinked'); onChanged(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not unlink'); } finally { setBusy(''); }
  };
  const linked = !!book?.id;
  const warn = (book?.warnings || []).length > 0;
  return (
    <div className={`rounded-xl border p-3 ${linked ? (book.error ? 'border-rose-300 bg-rose-50/60' : warn ? 'border-amber-300 bg-amber-50/60' : 'border-emerald-200 bg-white') : 'border-dashed border-slate-300 bg-slate-50/60'}`} data-testid={`fy-book-${year.year}-${kind.key}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-bold text-slate-900">{kind.label}</div>
        {linked ? (book.error ? <AlertTriangle className="w-4 h-4 text-rose-600" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />) : <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">not linked</span>}
      </div>
      {linked ? (
        <div className="mt-1.5 space-y-1 text-xs text-slate-600">
          <div className="flex items-center gap-1.5 font-semibold text-slate-800 truncate" title={book.title}>
            <a href={`https://docs.google.com/spreadsheets/d/${book.id}/edit`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-sky-700 truncate" data-testid={`fy-book-open-${year.year}-${kind.key}`}>{book.title || book.id}<ExternalLink className="w-3 h-3 shrink-0" /></a>
          </div>
          {kind.key === 'work_orders' && book.pm_tabs && <div>PM tabs: <b>{Object.keys(book.pm_tabs).join(', ') || '—'}</b></div>}
          {kind.key !== 'work_orders' && book.data_tab && <div>Tab: <b>{book.data_tab}</b></div>}
          {kind.key === 'purchase_orders' && book.first_number && <div>First PO #: <b>{book.first_number}</b></div>}
          {book.year_cells?.length > 0 && <div>Column A: <b>{book.year_cells.join(', ')}</b></div>}
          {book.error && <div className="text-rose-700 font-semibold" data-testid={`fy-book-error-${year.year}-${kind.key}`}>{book.error}</div>}
          {(book.warnings || []).map((w, i) => <div key={i} className="text-amber-800">{w}</div>)}
          {book.linked_by && <div className="text-slate-400">Linked by {book.linked_by}</div>}
          <div className="flex items-center gap-2 pt-1">
            <button onClick={verify} disabled={!!busy} className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-200 bg-white text-[11px] font-semibold hover:bg-slate-50 disabled:opacity-50" data-testid={`fy-book-verify-${year.year}-${kind.key}`}>{busy === 'verify' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Check</button>
            {canEdit && !year.builtin && <button onClick={unlink} disabled={!!busy} className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-200 bg-white text-[11px] font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50" data-testid={`fy-book-unlink-${year.year}-${kind.key}`}><Unlink className="w-3 h-3" /> Unlink</button>}
          </div>
        </div>
      ) : canEdit ? (
        <div className="mt-2 flex items-center gap-1.5">
          <input value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && link()} placeholder="Paste the Google Sheet link" className="flex-1 h-8 px-2 rounded-md border border-slate-300 bg-white text-xs min-w-0" data-testid={`fy-book-url-${year.year}-${kind.key}`} />
          <button onClick={link} disabled={!!busy || !url.trim()} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-sky-800 disabled:opacity-50" data-testid={`fy-book-link-${year.year}-${kind.key}`}>{busy === 'link' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />} Link</button>
        </div>
      ) : <p className="mt-2 text-xs text-slate-500">A super admin links this book.</p>}
    </div>
  );
}

export default function FiscalBooksPanel({ user }) {
  const [data, setData] = useState(null);
  const canEdit = user?.role === 'super_admin';
  const load = useCallback(async () => {
    try { setData((await axios.get(API, auth())).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load fiscal year books'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-4 text-sm text-slate-500">Loading fiscal year books…</div>;

  const next = data.years.find(y => y.year === data.next_fy);
  const soon = data.days_to_switch <= 45;
  const yearsToShow = data.years.filter(y => y.is_current || y.is_next || data.live_years.includes(y.year));
  return (
    <div className="ti-panel rounded-2xl p-4 space-y-4" data-testid="fiscal-books-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-black text-slate-900 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-sky-700" /> Fiscal Year Books</h3>
          <p className="text-xs text-slate-600 mt-1 max-w-3xl">
            Now in <b>FY{data.current_fy_long}</b> (numbers <b>{data.current_fy}-xxxx</b>). On <b>{fmtDate(data.switch_date)}</b> {data.days_to_switch > 0 ? <>(in <b>{data.days_to_switch} day{data.days_to_switch === 1 ? '' : 's'}</b>) </> : ''}
            new work orders, quotes and POs start coming from the <b>{data.next_fy_long}</b> books ({data.next_fy}-xxxx). The {data.current_fy_long} books stay live two-way until {fmtDate(data.current_live_until)}, then read-only hourly.
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm font-semibold hover:bg-slate-50" data-testid="fy-books-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      {next && !next.complete && (
        <div className={`rounded-xl border px-3 py-2 text-sm flex items-start gap-2 ${soon ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-700'}`} data-testid="fy-next-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>{data.next_fy_long} books not all linked.</b> From {fmtDate(data.switch_date)} nobody can start a new {['work_orders', 'quote_book', 'purchase_orders'].filter(k => !next.books[k]?.id).map(k => ({ work_orders: 'work order', quote_book: 'quote', purchase_orders: 'purchase order' })[k]).join(' / ')} in Ops until the book is linked here — existing {data.current_fy}- records keep working.
            {canEdit ? ' Make the sheet, share it with the same service account the 2026 books use (Editor), and paste the link below.' : ' Ask a super admin to link them.'}
          </div>
        </div>
      )}

      {yearsToShow.map(y => (
        <div key={y.year} className="space-y-2" data-testid={`fy-year-${y.year}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-black text-slate-900">FY{y.long}</span>
            <span className="text-xs text-slate-500">{fmtDate(y.start)} – {fmtDate(y.end)} · numbers {y.year}-xxxx</span>
            {y.is_current && <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">current</span>}
            {y.is_next && <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">next</span>}
            {!y.is_current && !y.is_next && <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">previous · still live</span>}
            {y.complete && <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">all three linked</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.kinds.map(k => <BookCard key={k.key} year={{ ...y, builtin: ['25', '26', '27'].includes(y.year) }} kind={k} book={y.books[k.key]} canEdit={canEdit} onChanged={setData} />)}
          </div>
        </div>
      ))}

      <NextYearBooks data={data} canEdit={canEdit} onChanged={setData} />
    </div>
  );
}

const KIND_LABEL = { purchase_orders: 'Purchase Orders', work_orders: 'Work Orders', quote_book: 'Quote Book' };

// "Make a note of the empty form so next year's books are easy": captured templates + one-click PO book creation.
function NextYearBooks({ data, canEdit, onChanged }) {
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState('');
  const loadTpl = useCallback(() => axios.get(`${API}/templates`, auth()).then(r => setTpl(r.data)).catch(() => setTpl({})), []);
  useEffect(() => { loadTpl(); }, [loadTpl]);
  if (!tpl) return null;
  const nextYear = data.next_fy;
  const nextRow = data.years.find(y => y.year === nextYear);
  const poLinked = !!nextRow?.books?.purchase_orders?.id;
  const po = tpl.purchase_orders;
  const capture = async (kind) => {
    setBusy(kind);
    try { await axios.post(`${API}/templates/capture/${kind}/${data.current_fy}`, {}, auth()); toast.success(`${KIND_LABEL[kind]} form noted from the FY${data.current_fy_long} book`); loadTpl(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not capture'); } finally { setBusy(''); }
  };
  const createPo = async () => {
    if (!window.confirm(`Create the ${data.next_fy_long} Purchase Orders sheet now? It gets 1,000 pre-numbered rows continuing from the last PO number, is shared to your email, and is linked as the FY${data.next_fy_long} PO book.`)) return;
    setBusy('create');
    try {
      const r = await axios.post(`${API}/create/purchase_orders/${nextYear}`, {}, auth());
      toast.success(`${r.data.book?.title} created — first PO ${r.data.book?.first_number}${r.data.book?.shared_with ? `, shared with ${r.data.book.shared_with}` : ''}`);
      onChanged(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create the book'); } finally { setBusy(''); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3" data-testid="fy-next-year-books">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h4 className="text-sm font-black text-slate-900 flex items-center gap-2"><FilePlus2 className="w-4 h-4 text-sky-700" /> Next year's books (FY{data.next_fy_long})</h4>
          <p className="text-xs text-slate-600 mt-0.5">Ops keeps a note of each book's empty form so next year's can be made in one click (POs) or by hand from the recipe (Work Orders, Quote Book).</p>
        </div>
        {canEdit && (
          <button onClick={createPo} disabled={!po || poLinked || !!busy} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-sky-800 disabled:opacity-40" data-testid="fy-create-po-book" title={poLinked ? `FY${data.next_fy_long} PO book already linked` : !po ? 'Capture the PO form first' : ''}>
            {busy === 'create' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FilePlus2 className="w-4 h-4" />} Create {data.next_fy_long} Purchase Orders book
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {['purchase_orders', 'work_orders', 'quote_book'].map(kind => {
          const t = tpl[kind];
          return (
            <div key={kind} className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5 text-xs text-slate-700 space-y-1" data-testid={`fy-template-${kind}`}>
              <div className="flex items-center justify-between gap-2">
                <b className="text-slate-900">{KIND_LABEL[kind]}</b>
                {canEdit && <button onClick={() => capture(kind)} disabled={!!busy} className="h-6 px-2 rounded border border-slate-300 bg-white text-[11px] font-semibold hover:bg-slate-100 disabled:opacity-50" data-testid={`fy-capture-${kind}`}>{busy === kind ? '…' : t ? 'Re-capture' : 'Capture form'}</button>}
              </div>
              {!t && <p className="text-slate-500">Not noted yet.</p>}
              {t && kind === 'purchase_orders' && (
                <>
                  <p>From <b>{t.from_title}</b> · noted {new Date(t.captured_at).toLocaleDateString()}</p>
                  <p>Tabs: <b>{t.tabs.join(' · ')}</b>{t.form_header?.length ? <> (form tab: {t.form_header.join(' | ')})</> : null}</p>
                  <p>Header: <span className="font-mono">{t.header.filter(Boolean).join(' | ')}</span></p>
                  <p>Column A pre-numbered from <b>{t.first_number}</b> (next book continues from the last PO used); {Object.entries(t.row_formulas || {}).map(([c, f]) => `${c} ${f.replace(/2\b/g, 'row')}`).join(', ')}; {Object.keys(t.number_formats || {}).join('/')} as currency; header bold; {t.frozen_rows} frozen row.</p>
                </>
              )}
              {t && kind !== 'purchase_orders' && (
                <>
                  <p>From <b>{t.from_title}</b> · noted {new Date(t.captured_at).toLocaleDateString()}</p>
                  <p>Tabs: <b>{t.tabs.join(' · ')}</b></p>
                  {Object.entries(t.heads || {}).slice(0, 1).map(([tab, rows]) => (
                    <div key={tab}>
                      <p className="text-slate-500">First rows of “{tab}”:</p>
                      {rows.slice(0, 3).map((r, i) => <p key={i} className="font-mono truncate" title={r.join(' | ')}>{r.map(c => String(c)).join(' | ')}</p>)}
                    </div>
                  ))}
                  <p className="text-slate-500">{kind === 'work_orders' ? 'One tab per PM ("Name - n"); row 1 header, row 2 "Year" with K = SUM(Sub-Total) (the leaderboard reads K2); numbered rows YY-7000+.' : 'Tab named for the year; row 2 header; rows pre-numbered 0YY-001… in columns A-C; column L = quote $ (summed in L1).'}</p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
